import { useCallback, useMemo } from 'react';

import { Modifier } from 'features/menu/components/product-detail/modifier/modifier';
import { IModifier, IModifierData } from 'features/menu/components/product-detail/modifier/types';
import {
  getValidModifiersForSelection,
  mapModifier,
} from 'features/menu/components/product-detail/modifier/utils';
import {
  IModifierGroup,
  ModifierGroup,
} from 'features/menu/components/product-detail/product-customization/product-customization-modifier-group';
import {
  GroupsCustomizationModifiersContainer,
  ModifiersCustomizationContainer,
} from 'features/menu/components/product-detail/product-customization/styled';
import {
  ComboSlotType,
  SelectModifierFn,
} from 'features/menu/components/product-detail/product-customization/types';
import {
  getComboSlotType,
  groupModifiersByDisplayName,
} from 'features/menu/components/product-detail/product-customization/utils';
import { useProductWizardContext } from 'features/menu/components/product-wizard';
import { DEFAULT_SLOT_KEY, SlotSelections } from 'features/menu/components/product-wizard/types';
import { useProductWizardFlow } from 'features/menu/components/product-wizard/use-product-wizard-flow';

import { ListCardGroup } from 'components/list-card-group';
import { ListCardGroupVariant } from 'components/list-card-group/types';
import { t } from 'state/intl/translations';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { hasOptionEntries, mapAvailableOptions } from 'state/unified-menu/utils';

export const ProductCustomizationModifiers = (props: { forceGroupModifiers?: boolean }) => {
  const { posServiceMode } = useMenuQueryOptions();
  const { indexedMenu } = useUnifiedMenuContext();
  const { slotsSelections, selectModifierMultiplier } = useProductWizardContext();
  const { currentFlowStep } = useProductWizardFlow();

  const slotKey = currentFlowStep.slotKey || DEFAULT_SLOT_KEY;
  const slotSelections = slotsSelections[slotKey];
  const selectedItemId = slotSelections?.itemId;
  const shouldGroupModifiers =
    props.forceGroupModifiers ||
    slotKey === DEFAULT_SLOT_KEY ||
    getComboSlotType(indexedMenu, slotKey) === ComboSlotType.PICKER_ASPECT;

  const modifiers = useMemo(() => {
    if (!selectedItemId) {
      return [];
    }

    const selectedItem = indexedMenu.get(selectedItemId);
    if (!hasOptionEntries(posServiceMode, selectedItem)) {
      return [];
    }

    const mappedModifiers = mapAvailableOptions(
      indexedMenu,
      posServiceMode,
      selectedItem.options.entries,
      ({ entryId, quantity }) =>
        mapModifier(
          indexedMenu,
          entryId,
          selectedItem.options.entries.length,
          posServiceMode,
          quantity
        )
    );

    // filter if null values
    const filteredMods: IModifier[] = (mappedModifiers as Array<IModifier | null>).filter(
      modifier => !!modifier && !!modifier.multipliers?.length
    );

    const validModifiers = getValidModifiersForSelection(filteredMods);
    return validModifiers;
  }, [indexedMenu, posServiceMode, selectedItemId]);

  const onModifierSelectionChange = useCallback<SelectModifierFn>(
    (selections, modifier) => {
      if (selectedItemId) {
        selectModifierMultiplier(slotKey, selectedItemId, modifier.id, selections);
      }
    },
    [selectModifierMultiplier, selectedItemId, slotKey]
  );

  if (modifiers.length === 0) {
    return null;
  }

  const Component = shouldGroupModifiers
    ? GroupsCustomizationModifiers
    : FlatCustomizationModifiers;

  return (
    <Component
      modifiers={modifiers}
      slotSelections={slotSelections}
      onModifierSelectionChange={onModifierSelectionChange}
    />
  );
};

export const GroupsCustomizationModifiers = ({
  modifiers,
  slotSelections,
  onModifierSelectionChange,
}: {
  modifiers: IModifier[];
  slotSelections: SlotSelections | undefined;
  onModifierSelectionChange: SelectModifierFn;
}) => {
  const modifierGroupsProps = useMemo<IModifierGroup[]>(() => {
    const modifierSelections = slotSelections?.modifiers ?? {};
    const groups = groupModifiersByDisplayName(modifiers, modifierSelections);
    return groups;
  }, [modifiers, slotSelections?.modifiers]);

  return (
    <GroupsCustomizationModifiersContainer>
      {modifierGroupsProps.map(modifierGroupProps =>
        modifierGroupProps.modifiers.length ? (
          <ModifierGroup
            {...modifierGroupProps}
            key={modifierGroupProps.key}
            onModifierSelectionChange={onModifierSelectionChange}
          />
        ) : null
      )}
    </GroupsCustomizationModifiersContainer>
  );
};

export const FlatCustomizationModifiers = ({
  modifiers,
  slotSelections,
  onModifierSelectionChange,
}: {
  modifiers: IModifier[];
  slotSelections: SlotSelections | undefined;
  onModifierSelectionChange: SelectModifierFn;
}) => {
  const modifiersData = useMemo<IModifierData[]>(() => {
    const modifierSelections = slotSelections?.modifiers ?? {};
    return modifiers.map(modifier => ({
      modifier,
      selections: modifierSelections[modifier.id] || {},
    }));
  }, [modifiers, slotSelections?.modifiers]);

  return (
    <ModifiersCustomizationContainer>
      <ListCardGroup
        isFullContainer
        items={modifiersData}
        variant={ListCardGroupVariant.SINGLE}
        header={t('customizations')}
        renderItem={({ item: modifierProps }: { item: IModifierData }) => {
          return (
            <Modifier
              key={`modifier-${modifierProps.modifier.id}`}
              {...modifierProps}
              onSelectionsChange={selections => {
                onModifierSelectionChange(selections, modifierProps.modifier);
              }}
            />
          );
        }}
      />
    </ModifiersCustomizationContainer>
  );
};
